import React from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { UseMutationResult } from '@tanstack/react-query';
import { DURATION_NORMAL, EASE_OUT } from '@/lib/motion';
import type {
  AuthorizeResponse,
  ConnectionStatus,
} from '../../types/integrations';

type StartConnectMutation = UseMutationResult<
  { status: 'success'; data: AuthorizeResponse },
  unknown,
  void,
  unknown
>;

interface ReconnectBannerProps {
  status: ConnectionStatus;
  startConnect: StartConnectMutation;
}

const STATUS_COPY: Record<ConnectionStatus, { title: string; body: string }> = {
  active: { title: '', body: '' },
  reauth_required: {
    title: 'Reconnect Needed',
    body: 'Your Google access has expired. Reconnect to keep events syncing.',
  },
  expired: {
    title: 'Reconnect Needed',
    body: 'Your Google access has expired. Reconnect to keep events syncing.',
  },
  revoked: {
    title: 'Connection Revoked',
    body: "Google revoked Tereiko's access. Reconnect to resume syncing.",
  },
};

const ReconnectBanner: React.FC<ReconnectBannerProps> = ({
  status,
  startConnect,
}) => {
  const copy = STATUS_COPY[status];
  if (!copy?.title) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
      className="flex items-start gap-3 p-4 rounded-2 bg-warning-500/10 dark:bg-warning-500/16 border border-warning-500/30"
      role="alert"
    >
      <ExclamationTriangleIcon className="w-5 h-5 text-warning-500 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary">{copy.title}</p>
        <p className="text-sm text-text-secondary mt-0.5">{copy.body}</p>
      </div>
      <button
        type="button"
        onClick={() => startConnect.mutate()}
        disabled={startConnect.isPending}
        className="btn btn-primary btn-sm shrink-0 active:scale-[0.97] transition-transform disabled:opacity-50"
      >
        {startConnect.isPending ? 'Opening…' : 'Reconnect'}
      </button>
    </motion.div>
  );
};

export default ReconnectBanner;
